import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { billsApi, categoriesApi, paymentsApi, exportApi } from '@/lib/api'
import { formatCurrency, downloadBlob } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Plus, CheckCircle, Trash2, CreditCard, XCircle, Pencil, ExternalLink, Download } from 'lucide-react'
import { Bill, Category } from '@/types'

export default function Bills() {
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const initialTab = searchParams.get('tab') || 'pending'
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isPayOpen, setIsPayOpen] = useState(false)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null)
  const [editingBill, setEditingBill] = useState<Bill | null>(null)
  const [selectedBills, setSelectedBills] = useState<number[]>([])

  const { data: bills = [] } = useQuery<Bill[]>({
    queryKey: ['bills'],
    queryFn: async () => {
      const res = await billsApi.getAll()
      return res.data
    },
  })

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ['categories', 'bill'],
    queryFn: async () => {
      const res = await categoriesApi.getAll('bill')
      return res.data
    },
  })

  const createBill = useMutation({
    mutationFn: billsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bills'] })
      setIsCreateOpen(false)
    },
  })

  const payBill = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => billsApi.markPaid(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bills'] })
      setIsPayOpen(false)
      setSelectedBill(null)
    },
  })

  const bulkPay = useMutation({
    mutationFn: ({ bill_ids, payment_date }: { bill_ids: number[]; payment_date: string }) =>
      billsApi.bulkPay(bill_ids, payment_date),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bills'] })
      setSelectedBills([])
    },
  })

  const deleteBill = useMutation({
    mutationFn: billsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bills'] })
    },
  })

  const updateBill = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => billsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bills'] })
      setIsEditOpen(false)
      setEditingBill(null)
    },
  })

  const unmarkPaid = useMutation({
    mutationFn: async (bill: Bill) => {
      // Get the most recent payment for this bill
      const payments = await paymentsApi.getAll({ bill_id: bill.id, status: 'paid' })
      if (payments.data.length > 0) {
        const lastPayment = payments.data[0]
        return paymentsApi.delete(lastPayment.id)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bills'] })
    },
  })

  // Filter bills by status (now comes from API)
  const activeBills = bills.filter((b: any) => b.active !== 0)
  const pendingBills = activeBills.filter((b: any) => b.status === 'pending')
  const overdueBills = activeBills.filter((b: any) => b.status === 'overdue')
  const paidBills = activeBills.filter((b: any) => b.status === 'paid')

  const handleCreateSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    createBill.mutate({
      name: formData.get('name') as string,
      amount: parseFloat(formData.get('amount') as string),
      due_day: parseInt(formData.get('due_day') as string),
      frequency: formData.get('frequency') as string,
      category_id: formData.get('category_id') ? parseInt(formData.get('category_id') as string) : undefined,
      autopay: formData.get('autopay') === 'on',
      notes: formData.get('notes') as string || undefined,
      merchant_pattern: formData.get('merchant_pattern') as string || undefined,
    })
  }

  const handlePaySubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!selectedBill) return
    const formData = new FormData(e.currentTarget)
    payBill.mutate({
      id: selectedBill.id,
      data: {
        payment_date: formData.get('payment_date') as string,
        confirmation_number: formData.get('confirmation_number') as string || undefined,
        amount_paid: parseFloat(formData.get('amount_paid') as string),
      },
    })
  }

  const handleBulkPay = () => {
    if (selectedBills.length === 0) return
    bulkPay.mutate({
      bill_ids: selectedBills,
      payment_date: new Date().toISOString().split('T')[0],
    })
  }

  const handleEditSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!editingBill) return
    const formData = new FormData(e.currentTarget)
    updateBill.mutate({
      id: editingBill.id,
      data: {
        name: formData.get('name') as string,
        amount: parseFloat(formData.get('amount') as string),
        due_day: parseInt(formData.get('due_day') as string),
        frequency: formData.get('frequency') as string,
        category_id: formData.get('category_id') ? parseInt(formData.get('category_id') as string) : null,
        autopay: formData.get('autopay') === 'on',
        notes: formData.get('notes') as string || null,
        merchant_pattern: formData.get('merchant_pattern') as string || null,
      },
    })
  }

  const toggleBillSelection = (billId: number) => {
    setSelectedBills(prev =>
      prev.includes(billId)
        ? prev.filter(id => id !== billId)
        : [...prev, billId]
    )
  }

  const handleExportCsv = async () => {
    try {
      const response = await exportApi.billsCsv()
      downloadBlob(response.data, `bills-${new Date().toISOString().split('T')[0]}.csv`)
    } catch (error) {
      console.error('Export failed:', error)
    }
  }

  const BillCard = ({ bill }: { bill: any }) => {
    const dueDate = bill.due_date ? new Date(bill.due_date) : null
    const dueDateStr = dueDate ? dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : `Day ${bill.due_day}`

    return (
    <Card className={`${bill.status === 'overdue' ? 'border-destructive' : bill.status === 'paid' ? 'border-green-500/50' : ''}`}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Checkbox
              checked={selectedBills.includes(bill.id)}
              onCheckedChange={() => toggleBillSelection(bill.id)}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-sm truncate">{bill.name}</h3>
                {bill.category_name && (
                  <span
                    className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded text-xs shrink-0"
                    style={{
                      backgroundColor: `${bill.category_color}20`,
                      color: bill.category_color || undefined,
                    }}
                  >
                    {bill.category_name}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Due: {dueDateStr} · {bill.frequency?.charAt(0).toUpperCase() + bill.frequency?.slice(1)}
                {bill.days_until_due !== undefined && bill.status !== 'paid' && (
                  <span className={bill.days_until_due < 0 ? 'text-destructive ml-1' : bill.days_until_due <= 3 ? 'text-yellow-600 ml-1' : ''}>
                    ({bill.days_until_due < 0
                      ? `${Math.abs(bill.days_until_due)} ${Math.abs(bill.days_until_due) === 1 ? 'day' : 'days'} overdue`
                      : bill.days_until_due === 0
                        ? 'today'
                        : `${bill.days_until_due} ${bill.days_until_due === 1 ? 'day' : 'days'}`})
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <p className="font-semibold text-sm">{formatCurrency(bill.amount)}</p>
            <div className="flex gap-1">
              {bill.status !== 'paid' && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => {
                    setSelectedBill(bill)
                    setIsPayOpen(true)
                  }}
                >
                  <CheckCircle className="h-4 w-4" />
                </Button>
              )}
              {bill.status === 'paid' && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => unmarkPaid.mutate(bill)}
                  title="Unmark as paid"
                >
                  <XCircle className="h-4 w-4" />
                </Button>
              )}
              {bill.url && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 text-primary"
                  asChild
                  title="Pay online"
                >
                  <a href={bill.url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              )}
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                onClick={() => {
                  setEditingBill(bill)
                  setIsEditOpen(true)
                }}
                title="Edit bill"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )}

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Bills</h1>
          <p className="text-muted-foreground">Manage your recurring bills</p>
        </div>
        <div className="flex gap-2">
          {selectedBills.length > 0 && (
            <Button onClick={handleBulkPay} variant="outline">
              <CreditCard className="mr-2 h-4 w-4" />
              Pay {selectedBills.length} Bills
            </Button>
          )}
          <Button variant="outline" onClick={handleExportCsv}>
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add Bill
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Bill</DialogTitle>
                <DialogDescription>Create a new bill to track</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input id="name" name="name" required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="amount">Amount</Label>
                    <Input
                      id="amount"
                      name="amount"
                      type="number"
                      step="0.01"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="due_day">Due Day (1-31)</Label>
                    <Input
                      id="due_day"
                      name="due_day"
                      type="number"
                      min="1"
                      max="31"
                      placeholder="e.g., 15"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="frequency">Frequency</Label>
                    <Select name="frequency" defaultValue="monthly">
                      <SelectTrigger>
                        <SelectValue placeholder="Select frequency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="biweekly">Bi-weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="semiannual">Semi-annual</SelectItem>
                        <SelectItem value="annual">Annual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category_id">Category</Label>
                    <Select name="category_id">
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id.toString()}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="autopay" name="autopay" />
                  <Label htmlFor="autopay">Auto-pay enabled</Label>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Input id="notes" name="notes" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="merchant_pattern">Auto-Pay Pattern</Label>
                  <Input
                    id="merchant_pattern"
                    name="merchant_pattern"
                    placeholder="e.g., UGI, XFINITY (matches transactions)"
                  />
                  <p className="text-xs text-muted-foreground">
                    Transactions matching this pattern will auto-mark this bill as paid
                  </p>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={createBill.isPending}>
                    {createBill.isPending ? 'Creating...' : 'Create Bill'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs defaultValue={initialTab}>
        <TabsList>
          <TabsTrigger value="pending">
            Pending ({pendingBills.length})
          </TabsTrigger>
          <TabsTrigger value="overdue">
            Overdue ({overdueBills.length})
          </TabsTrigger>
          <TabsTrigger value="paid">
            Paid ({paidBills.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4">
          {pendingBills.length === 0 ? (
            <p className="text-muted-foreground">No pending bills</p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {pendingBills.map((bill) => <BillCard key={bill.id} bill={bill} />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="overdue" className="mt-4">
          {overdueBills.length === 0 ? (
            <p className="text-muted-foreground">No overdue bills</p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {overdueBills.map((bill) => <BillCard key={bill.id} bill={bill} />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="paid" className="mt-4">
          {paidBills.length === 0 ? (
            <p className="text-muted-foreground">No paid bills</p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {paidBills.map((bill) => <BillCard key={bill.id} bill={bill} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Pay Bill Dialog */}
      <Dialog open={isPayOpen} onOpenChange={setIsPayOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark as Paid</DialogTitle>
            <DialogDescription>
              {selectedBill?.name} - {selectedBill && formatCurrency(selectedBill.amount)}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handlePaySubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="payment_date">Payment Date</Label>
              <Input
                id="payment_date"
                name="payment_date"
                type="date"
                defaultValue={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="amount_paid">Amount Paid</Label>
              <Input
                id="amount_paid"
                name="amount_paid"
                type="number"
                step="0.01"
                defaultValue={selectedBill?.amount}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmation_number">Confirmation Number</Label>
              <Input
                id="confirmation_number"
                name="confirmation_number"
                placeholder="Optional"
              />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={payBill.isPending}>
                {payBill.isPending ? 'Processing...' : 'Mark Paid'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Bill Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Bill</DialogTitle>
            <DialogDescription>Update bill details</DialogDescription>
          </DialogHeader>
          {editingBill && (
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit_name">Name</Label>
                <Input id="edit_name" name="name" defaultValue={editingBill.name} required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_amount">Amount</Label>
                  <Input
                    id="edit_amount"
                    name="amount"
                    type="number"
                    step="0.01"
                    defaultValue={editingBill.amount}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_due_day">Due Day (1-31)</Label>
                  <Input
                    id="edit_due_day"
                    name="due_day"
                    type="number"
                    min="1"
                    max="31"
                    defaultValue={editingBill.due_day}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_frequency">Frequency</Label>
                  <Select name="frequency" defaultValue={editingBill.frequency}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="biweekly">Bi-weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="semiannual">Semi-annual</SelectItem>
                      <SelectItem value="annual">Annual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_category_id">Category</Label>
                  <Select name="category_id" defaultValue={editingBill.category_id?.toString() || ''}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id.toString()}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="edit_autopay" name="autopay" defaultChecked={editingBill.autopay} />
                <Label htmlFor="edit_autopay">Auto-pay enabled</Label>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_notes">Notes</Label>
                <Input id="edit_notes" name="notes" defaultValue={editingBill.notes || ''} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_merchant_pattern">Auto-Pay Pattern</Label>
                <Input
                  id="edit_merchant_pattern"
                  name="merchant_pattern"
                  defaultValue={editingBill.merchant_pattern || ''}
                  placeholder="e.g., UGI, XFINITY (matches transactions)"
                />
                <p className="text-xs text-muted-foreground">
                  Transactions matching this pattern will auto-mark this bill as paid
                </p>
              </div>
              <DialogFooter className="flex justify-between sm:justify-between">
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => {
                    if (confirm(`Are you sure you want to delete "${editingBill.name}"?`)) {
                      deleteBill.mutate(editingBill.id)
                      setIsEditOpen(false)
                      setEditingBill(null)
                    }
                  }}
                  disabled={deleteBill.isPending}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
                <Button type="submit" disabled={updateBill.isPending}>
                  {updateBill.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
